import {
  MultiPolygon2,
  NumberWithPoints2Result,
  Point2,
  Polyline2,
  ResultType,
} from '../../geometric-types';

const EPSILON = 1e-9;

function areSamePoint(a: Point2, b: Point2): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

function uniqueVerticesForClosedRing(ring: Polyline2): Polyline2 {
  // Remove duplicated closing point if present
  let vertices =
    ring.length >= 2 && areSamePoint(ring[0], ring[ring.length - 1])
      ? ring.slice(0, ring.length - 1)
      : ring;

  // Deduplicate consecutive identical vertices inside the ring
  const deduped: Polyline2 = [];
  for (let i = 0; i < vertices.length; i++) {
    const current = vertices[i];
    const prev = deduped.length ? deduped[deduped.length - 1] : undefined;
    if (!prev || !areSamePoint(prev, current)) {
      deduped.push(current);
    }
  }
  return deduped;
}

/**
 * Find all corners (non-collinear vertices) in a ring.
 * Returns the corner points.
 */
function findCornersInRing(ring: Polyline2): Point2[] {
  // Need at least 4 points for a closed triangle ring (including duplicate)
  if (ring.length < 4) return [];

  const vertices = uniqueVerticesForClosedRing(ring);
  if (vertices.length < 3) return [];

  const corners: Point2[] = [];
  const n = vertices.length;

  for (let i = 0; i < n; i++) {
    const prev = vertices[(i - 1 + n) % n];
    const curr = vertices[i];
    const next = vertices[(i + 1) % n];

    const v1x = curr[0] - prev[0];
    const v1y = curr[1] - prev[1];
    const v2x = next[0] - curr[0];
    const v2y = next[1] - curr[1];

    const cross = v1x * v2y - v1y * v2x;
    const len1 = Math.hypot(v1x, v1y);
    const len2 = Math.hypot(v2x, v2y);

    if (len1 < EPSILON || len2 < EPSILON) continue;
    if (Math.abs(cross) > EPSILON) corners.push(curr);
  }

  return corners;
}

/**
 * Calculate the number of external corners in a polygon.
 * An external corner is any non-collinear vertex.
 * Also returns the corner points for visualization.
 */
export function calculateSheetShapeNumberOfExternalCorners(
  shape: MultiPolygon2
): NumberWithPoints2Result {
  const allCorners: Point2[] = [];

  for (const polygon of shape.polygons) {
    const corners = findCornersInRing(polygon.exterior);
    allCorners.push(...corners);
  }

  return {
    type: ResultType.NUMBER,
    value: allCorners.length,
    points: allCorners,
  };
}
